// Tên + mô tả sản phẩm CNV Work — MỘT nguồn cho mọi chỗ hiển thị tên gói.
//
// Vì sao không đọc thẳng `display_name` của BE: (1) chuỗi BE KHÔNG đi qua Lingui ⇒ mở màn
// EN sẽ lòi tiếng Việt giữa trang; (2) tên trong `meta.plans` là tên KỸ THUẬT cho operator
// ("CNV Work On Cloud · All-in-one", "CNV Work — Mua thêm user") — thừa tiền tố sản phẩm khi
// hiện ngay trong chính sản phẩm đó.
//
// Tách khỏi MarketplacePage 15/08/2026 khi đưa gói lên thanh trên cùng: để map ở lại trang
// Kho ứng dụng thì thanh trên cùng phải chép bản thứ hai ⇒ cùng một gói mang 2 tên ở 2 chỗ
// cách nhau một màn hình. Sửa tên gói từ nay chỉ sửa Ở ĐÂY.
//
// Nằm trong KERNEL (cnv-ui) chứ không ở app: thanh trên cùng của SaaS (PlanBadgeV2) và trang
// Kho ứng dụng đều đọc bảng này; app chỉ import, không giữ bản sao.
//
// Gói lạ / gói mới chưa khai ở đây: rơi về tên BE gửi (thà tiếng Việt còn hơn trống). Gói đã
// ngưng bán mà dòng `meta.plans` bị xoá: BE có `RETIRED_NAMES` chống lòi mã thô, nhưng vẫn
// phải khai ở đây để bản EN đọc được — xem ADR-036.

import { useLingui } from '@lingui/react/macro';

/** Bảng tên/mô tả theo plan_code. Hook vì chuỗi phải đi qua macro `t`. */
function useCopyMap() {
  const { t } = useLingui();
  return {
    // Workspace đăng ký từ 15/09/2026 — thay "Freemium trọn đời" (sếp Phú chốt).
    trial: {
      name: t`Dùng thử 14 ngày`,
      // Không giới hạn user từ 19/10/2026 (sếp Phú) — trước đó 10.
      tagline: t`Đủ tính năng Work trong 14 ngày — không giới hạn user, 1GB. Hết hạn chọn gói để dùng tiếp.`,
    },
    free: {
      name: t`Freemium`,
      tagline: t`Gói miễn phí của workspace đăng ký trước 15/09/2026 — 10 user, 1GB.`,
    },
    'cnvwork-allinone': {
      name: t`On-Cloud All-in-one`,
      tagline: t`CRM + HRM + Chat AI + Task + Approval. Thuê theo năm, đơn giá giảm theo bậc user.`,
    },
    'cnvwork-crm-only': {
      name: t`On-Cloud CRM Only`,
      tagline: t`Chỉ module bán hàng. Thuê theo năm, đơn giá giảm theo bậc user.`,
    },
    unlimited: {
      name: t`Unlimited`,
      // Trần 250 user — sếp Phú chốt 07/10/2026 (trước đó ghi «không giới hạn user»).
      tagline: t`Đầu tư một lần, tối đa 250 user.`,
    },
    'cnvwork-unlimited-chat': {
      name: t`Unlimited & Chat đa kênh`,
      tagline: t`Bản Unlimited kèm chat đa kênh không giới hạn Page.`,
    },
    'cnvwork-seat-addon': {
      name: t`Mua thêm user`,
      tagline: t`Thêm chỗ ngồi cho gói On-Cloud đang dùng, tính theo đơn giá bậc hiện tại.`,
    },
    'cnvwork-chat-pages': {
      name: t`Chat đa kênh`,
      tagline: t`Mỗi Page = 1 fanpage / kênh chat nối vào Hộp thư. Mua tối thiểu 3 tháng.`,
    },
    'cnvwork-storage': {
      name: t`Dung lượng bổ sung`,
      tagline: t`Thêm chỗ lưu file, hình ảnh, lịch sử chat.`,
    },
    'cnvwork-cnv-call-activate': {
      name: t`Call Center — khởi tạo hệ thống`,
      tagline: t`Dựng tổng đài cho workspace. Trả một lần, tích hợp miễn phí.`,
    },
    'cnvwork-cnv-call-seat': {
      name: t`Máy nhánh VOIP`,
      tagline: t`Gọi ra / nhận cuộc từ số nhà mạng ngay trong CNV Work.`,
    },
    'cnvwork-zcc-seat': {
      name: t`Máy nhánh Zalo Call Center`,
      tagline: t`Gọi tới khách bằng chính Zalo OA của bạn.`,
    },
    'cnvwork-zalo-auto-message': {
      name: t`Gửi tin nhắn tự động qua Zalo`,
      tagline: t`Gửi tin hàng loạt vào nhóm / bạn bè Zalo từ ứng dụng CNV Work trên máy tính. Mỗi gói 3 người dùng.`,
    },
    // Gói ĐÃ NGƯNG BÁN — chỉ còn trên license lịch sử.
    pro: {
      name: t`Pro (gói cũ, đã ngưng bán)`,
      tagline: null,
    },
    'cnvwork-cnv-call': {
      name: t`CNV Call (gói cũ, đã tách thành Khởi tạo + Máy nhánh)`,
      tagline: null,
    },
  };
}

/** Tên + mô tả cho 1 item của kho ứng dụng (`{ key, name, tagline }` do BE trả). */
export function useItemCopy(item) {
  const copy = useCopyMap()[item?.key];
  return {
    name: copy?.name || item?.name || '',
    tagline: copy?.tagline || item?.tagline || null,
  };
}

/** Chỉ tên gói, tra theo plan_code — dùng cho thanh trên cùng / nơi chỉ cần cái tên. */
export function usePlanName(plan_code, fallback = null) {
  return useCopyMap()[plan_code]?.name || fallback || plan_code || '';
}

/**
 * Hàm tra tên gói cho NHIỀU dòng trong CÙNG một lần render (danh sách workspace, bảng
 * license…). Gọi `usePlanName` trong `map` là gọi hook số lần thay đổi theo độ dài danh
 * sách — Rules of Hooks cấm, và lint:hooks chặn. Hook này gọi hook ĐÚNG MỘT LẦN rồi trả về
 * hàm thường để tra thoải mái.
 */
export function usePlanNameLookup() {
  const copy = useCopyMap();
  return (plan_code, fallback = null) => copy[plan_code]?.name || fallback || plan_code || '';
}
